import React, { PropsWithChildren, useContext, useMemo, useState } from 'react';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Button, Text } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { CustomDivider } from "@/components/CustomDivider";
import { ExternalPathString, Link } from 'expo-router';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppUtils from '@/utilities/AppUtils';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setDrawerOpen } from '@/state/user';
import AppBackground from '@/components/AppBackground';
import { Image, ImageSource } from 'expo-image';
import { useRouter } from 'expo-router';
import { setLoggedIn } from '@/state/user';
import { updateVisuals } from '@/state/uploads';
import { RabblLink } from '@/components/RabblLink';
import Badge from '@/components/Badge';
import { Community, UNKNOWN_COMMUNITY } from '@/types/community';
import { UNKNOWN_COMMUNITY_MEMBERSHIP } from '@/types/community_membership';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Drawer } from 'react-native-drawer-layout';
import { BasicScrollView } from './BasicScrollView';
import { AppContext } from '@/components/AppContext';
import { RelativePathString } from 'expo-router';
//import QRCode from "react-qr-code";
import { ExternalLink } from '@/components/ExternalLink';

// Anything more will overflow on small phones.
// Seems like the Drawer component has a dynamic size
// based on screen width, so we could try to figure out that
// algorithm and then make this size dynamic to be bigger
// with bigger drawers.
const QR_IMAGE_SIZE = 175;
const QR_IMAGE_SIZE_DENSITY_2 = QR_IMAGE_SIZE * 2;
const QR_IMAGE_SIZE_DENSITY_3 = QR_IMAGE_SIZE * 3;
const QR_IMAGE_SIZE_DENSITY_4 = QR_IMAGE_SIZE * 4;

const QR_IMAGE_LINK_DENSITY = QR_IMAGE_SIZE_DENSITY_3;

const NORMAL_LINKS = AppUtilities.isWeb();

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "90%",
    gap: 20,
    alignItems: 'center',
    alignSelf: 'center',
  },
  avatar: {
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: 'transparent',
  },
  contentWrapper: {
    padding: 6,
  },
  closeIconWrapper: {
    margin: 10,
    width: 8,
    height: 7,
  },
  closeIconView: {
    width: "100%",
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: AppUtilities.isWeb() ? 0 : 10,
  },
  membershipView: {
    flexDirection: 'row',
    padding: 5,
  },
  selectedCommunityText: {
    fontWeight: 'bold',
  },
  badge: {
    backgroundColor: 'red',
    borderRadius: 10,
    paddingHorizontal: 5,
    paddingVertical: 1,
  },
  badgeText: {
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },
  row: {
    gap: 10,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    width: '100%',
  },
  w11: {
    width: 11,
    marginHorizontal: 2,
  },
  w11h10: {
    width: 11,
    height: 10,
    marginHorizontal: 2,
  },
  w16h16: {
    width: 16,
    height: 16,
  },
  body: {
    gap: 2,
  },
  scrollView: {
    padding: 20,
    paddingTop: 10,
    flexGrow: 1,
    gap: 5,
  },
  topView: {
    gap: 2,
    paddingTop: AppUtilities.isWeb() ? 0 : 30,
    alignItems: 'center',
  },
  linksWrapper: {
    width: '100%',
    alignItems: "center",
    gap: 20,
    paddingTop: 20,
  },
  qr: {
    gap: 10,
    alignItems: "center",
  },
  gap: {
    gap: 20,
  },
  qrimage: {
    width: QR_IMAGE_SIZE,
    height: QR_IMAGE_SIZE,
  },
});

function UserPicture() : React.ReactNode {
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  const dispatch = useDispatch();
  const router = useRouter();
  const { showSpinner, hideSpinner } = useContext(AppContext);

  const onClick = () => {
    let doHideSpinner = true;
    try {
      showSpinner();
      doHideSpinner = AppUtils.closeDrawerActionRedux(
        () => {
          router.navigate("/myprofile");
        },
        hideSpinner,
        dispatch,
        setDrawerOpen,
      );
    } finally {
      if (doHideSpinner) {
        hideSpinner();
      }
    }
  };

  const directNavigate = () => {
    dispatch(setDrawerOpen(false));
    router.navigate("/myprofile");
  };

  if (AppUtils.isValidCommunityMembership(communityMembership) &&
      AppUtils.isValidProfile(communityMembership.profile) &&
      communityMembership.profile.visuals &&
      communityMembership.profile.visuals.length > 0 &&
      communityMembership.profile.visuals[0].uniqueid &&
      communityMembership.profile.visuals[0].uniqueid.length > 0) {

    if (NORMAL_LINKS) {
      return (
        <>
          <Link href="/myprofile" asChild={AppUtilities.isWeb()}>
            <Pressable onPress={directNavigate}>
              <Image
                source={{ uri: AppUtils.createBackendLink('/rabbl/api/visual?thumbnail=yes&uniqueid=' + communityMembership.profile.visuals[0].uniqueid) }}
                style={styles.avatar}
              />
            </Pressable>
          </Link>
          <View style={AppConstants.STYLES.padding5} />
        </>
      );
    } else {
      return (
        <Pressable onPress={onClick}>
          <Image
            source={{ uri: AppUtils.createBackendLink('/rabbl/api/visual?thumbnail=yes&uniqueid=' + communityMembership.profile.visuals[0].uniqueid) }}
            style={styles.avatar}
          />
          <View style={AppConstants.STYLES.padding5} />
        </Pressable>
      );
    }
  } else {
    return null;
  }
}

function UserHeader() : React.ReactNode {
  const user_info = useSelector((state: RootState) => state.user.user_info);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);
  if (AppUtils.isValidCommunityMembership(communityMembership) &&
      AppUtils.isValidProfile(communityMembership.profile) &&
      AppUtils.isValidUserInfo(communityMembership.user_info)) {
    return (
      <>
        <View style={{ justifyContent: "center", alignItems: "center", gap: 8 }}>
          <AppText>{communityMembership.profile.name}</AppText>
          { false &&
              <AppText>{communityMembership.user_info.email}</AppText>
          }
        </View>
        <View style={AppConstants.STYLES.padding10} />
        <CustomDivider fullWidth={true} />
      </>
    );
  } else if (AppUtils.isValidUserInfo(user_info)) {
    return (
      <>
        <View style={{ justifyContent: "center", alignItems: "center" }}>
          <AppText>{user_info.email}</AppText>
        </View>
        <View style={AppConstants.STYLES.padding5} />
        <CustomDivider fullWidth={true} />
      </>
    );
  } else {
    return null;
  }
}

function DomainName() : React.ReactNode {
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const community = useSelector((state: RootState) => state.user.current_community);
  if (isMainLogicalHost) {
    return (
      <>
        <View style={AppConstants.STYLES.centeredRow}>
          <CustomText>Rabbl</CustomText>
        </View>
        <View style={AppConstants.STYLES.padding5} />
      </>
    );
  } else {
    return (
      <>
        <View style={AppConstants.STYLES.centeredRow}>
          <CustomText>{community.name}</CustomText>
        </View>
        <View style={AppConstants.STYLES.padding5} />
      </>
    );
  }
}

function RabblsList() : React.ReactNode {
  const communityMemberships = useSelector((state: RootState) => state.user.communityMemberships);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);

  let existingCommunityMembershipId = UNKNOWN_COMMUNITY_MEMBERSHIP.uniqueid;
  if (AppUtils.isValidCommunityMembership(communityMembership)) {
    existingCommunityMembershipId = communityMembership.uniqueid;
  }

  if (communityMemberships &&
      communityMemberships.length > 0) {
    return (
      <>
        <View style={AppConstants.STYLES.padding10}>
          <CustomText>Your Rabbls</CustomText>
        </View>
        {
          communityMemberships.map((c) =>
            <RabblLink key={c.uniqueid} community={c.community ?? UNKNOWN_COMMUNITY} ensureDrawerDismissed={true}>
              <View style={styles.row}>
                {
                  c.uniqueid == existingCommunityMembershipId ?
                      (
                        <>
                          <Image source={require('@/assets/images/butterflyicon16x16.png')} style={styles.w16h16} />
                          <AppText style={styles.selectedCommunityText}>{c.community?.name}</AppText>
                          <Badge val={c.unread ?? 0} />
                        </>
                      )
                    :
                      (
                        <>
                          <Image source={require('@/assets/images/butterflyicon16x16.png')} style={styles.w16h16} />
                          <AppText>{c.community?.name}</AppText>
                          <Badge val={c.unread ?? 0} />
                        </>
                      )
                }
              </View>
            </RabblLink>
          )
        }
        <View style={AppConstants.STYLES.padding5} />
        <CustomDivider fullWidth={true} />
        <View style={AppConstants.STYLES.padding5} />
      </>
      );
  } else {
    return null;
  }
}

function HomeRabblItem() : React.ReactNode {
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  if (!isMainLogicalHost) {
    return (
      <>
        <RabblLink key={-1} community={UNKNOWN_COMMUNITY} ensureDrawerDismissed={true}>
          <View style={styles.row}>
            <Image source={require('@/assets/images/butterflyicon16x16.png')} style={styles.w16h16} />
            <AppText>Rabbl Homepage</AppText>
          </View>
        </RabblLink>
        <View style={AppConstants.STYLES.padding3} />
        <CustomDivider fullWidth={true} />
        <View style={AppConstants.STYLES.padding2} />
      </>
    );
  } else {
    return null;
  }
}

function getQRImageSources(template: string) : ImageSource[] {
  return [
    { uri: AppUtils.createBackendLink(template + QR_IMAGE_SIZE), width: QR_IMAGE_SIZE, height: QR_IMAGE_SIZE },
    { uri: AppUtils.createBackendLink(template + QR_IMAGE_SIZE_DENSITY_2), width: QR_IMAGE_SIZE_DENSITY_2, height: QR_IMAGE_SIZE_DENSITY_2 },
    { uri: AppUtils.createBackendLink(template + QR_IMAGE_SIZE_DENSITY_3), width: QR_IMAGE_SIZE_DENSITY_3, height: QR_IMAGE_SIZE_DENSITY_3 },
    { uri: AppUtils.createBackendLink(template + QR_IMAGE_SIZE_DENSITY_4), width: QR_IMAGE_SIZE_DENSITY_4, height: QR_IMAGE_SIZE_DENSITY_4 },
  ];
}

interface Props {
};

export default function DrawerComponent({ children }: PropsWithChildren<Props>) : React.ReactNode {
  const drawerOpen = useSelector((state: RootState) => state.user.drawerOpen);
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const isMainLogicalHost = useSelector((state: RootState) => state.user.isMainLogicalHost);
  const community = useSelector((state: RootState) => state.user.current_community);
  const dispatch = useDispatch();
  const router = useRouter();
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const communityMembership = useSelector((state: RootState) => state.user.communityMembership);

  const qr = useMemo(() => {

    let link : string = "";
    let qrlink : string = "";
    let qrImagesSources : ImageSource[] = [];
    if (isMainLogicalHost || !AppUtils.isValidCommunity(community)) {
      link = AppUtilities.getActualHostLink(true, true);
      const qrtemplate = "/rabbl/api/qr?size=";
      qrlink = AppUtils.createBackendLink(qrtemplate + QR_IMAGE_LINK_DENSITY);
      qrImagesSources = getQRImageSources(qrtemplate);
    } else {
      if (AppUtils.isValidCommunityMembership(communityMembership) && AppUtils.isCommunityMembershipFullyPaid(communityMembership)) {
        link = AppUtils.getCommunityURL(community) + "/r/" + communityMembership.uniqueid;
        const qrtemplate = "/rabbl/api/qr?domain=" + community.domain + "&member=" + communityMembership.uniqueid + "&size=";
        qrlink = AppUtils.createBackendLink(qrtemplate + QR_IMAGE_LINK_DENSITY);
        qrImagesSources = getQRImageSources(qrtemplate);
      } else {
        link = AppUtils.getCommunityURL(community);
        const qrtemplate = "/rabbl/api/qr?domain=" + community.domain + "&size=";
        qrlink = AppUtils.createBackendLink(qrtemplate + QR_IMAGE_LINK_DENSITY);
        qrImagesSources = getQRImageSources(qrtemplate);
      }
    }

    return (
      <View style={styles.qr}>
        <ExternalLink href={qrlink} target="_blank">
          <Image
            source={qrImagesSources}
            style={styles.qrimage}
          />
        </ExternalLink>
        <Link
          href={link as ExternalPathString}
          target="_blank"
          style={[CustomTextStyles.linkSmall, { maxWidth: QR_IMAGE_SIZE }]}
        >{link}</Link>
      </View>
    );
  }, [isMainLogicalHost, community, communityMembership]);

  const logout = async () => {
    let doHideSpinner = true;
    try {
      showSpinner();
      doHideSpinner = AppUtils.closeDrawerActionRedux(
        async () => {
          await AppUtils.logout(dispatch, showSpinner, hideSpinner);
          router.replace("/loggedout");
        },
        hideSpinner,
        dispatch,
        setDrawerOpen,
      );
    } finally {
      if (doHideSpinner) {
        hideSpinner();
      }
    }
  };

  const simpleNavigate = async (path: RelativePathString) => {
    let doHideSpinner = true;
    try {
      showSpinner();
      doHideSpinner = AppUtils.closeDrawerActionRedux(
        () => {
          router.navigate(path);
        },
        hideSpinner,
        dispatch,
        setDrawerOpen,
      );
    } finally {
      if (doHideSpinner) {
        hideSpinner();
      }
    }
  };

  const login = async () => {
    let doHideSpinner = true;
    try {
      showSpinner();
      doHideSpinner = AppUtils.closeDrawerActionRedux(
        () => {
          router.navigate("/signin");
        },
        hideSpinner,
        dispatch,
        setDrawerOpen,
      );
    } finally {
      if (doHideSpinner) {
        hideSpinner();
      }
    }
  };

  const join = async () => {
    let doHideSpinner = true;
    try {
      showSpinner();
      doHideSpinner = AppUtils.closeDrawerActionRedux(
        () => {
          if (isMainLogicalHost) {
            router.navigate("/signup");
          } else {
            router.navigate("/join");
          }
        },
        hideSpinner,
        dispatch,
        setDrawerOpen,
      );
    } finally {
      if (doHideSpinner) {
        hideSpinner();
      }
    }
  };

  const directNavigate = (path: string) => {
    dispatch(setDrawerOpen(false));
    router.navigate(path as RelativePathString);
  };

  return (
    // https://reactnavigation.org/docs/drawer-layout/
    <Drawer
      open={drawerOpen}
      onOpen={() => dispatch(setDrawerOpen(true))}
      onClose={() => dispatch(setDrawerOpen(false))}
      drawerPosition="right"
      renderDrawerContent={() => {
        return (
          <BasicScrollView center={false} overrideStyle={true} contentContainerStyle={styles.scrollView}>
            <View style={styles.topView}>
              <View style={styles.closeIconView}>
                <Pressable onPress={() => dispatch(setDrawerOpen(false))}>
                  <Image source={require("@/assets/images/close8x7.png")} style={styles.closeIconWrapper} />
                </Pressable>
              </View>

              {
                loggedIn ?
                    (
                      <>
                        <DomainName />
                        <UserPicture />
                        <UserHeader />
                      </>
                    )
                  :
                    (
                      <>
                        <DomainName />
                        <View style={AppConstants.STYLES.padding5} />
                        <CustomDivider fullWidth={true} />
                      </>
                    )
              }
            </View>
            {
              loggedIn ?
                  (
                    <>
                      <RabblsList />
                      <HomeRabblItem />
                      {
                        NORMAL_LINKS ?
                            <Link href="/settings" asChild={true} className={"nontext"}>
                              <Button onPress={() => directNavigate("/settings")}>Settings</Button>
                            </Link>
                          :
                            <Button onPress={() => simpleNavigate("/settings" as RelativePathString)}>Settings</Button>
                      }
                      <View style={AppConstants.STYLES.padding5} />
                      {
                        NORMAL_LINKS ?
                            <Link href="/signout" asChild={true} className={"nontext"}>
                              <Button onPress={() => directNavigate("/signout")}>Logout</Button>
                            </Link>
                          :
                            <Button onPress={logout}>Logout</Button>
                      }
                    </>
                  )
                :
                  (
                    <>
                      <View style={AppConstants.STYLES.padding2} />
                      <HomeRabblItem />

                      <View style={AppConstants.STYLES.padding2} />
                      {
                        NORMAL_LINKS ?
                            <Link href="/signin" asChild={true} className={"nontext"}>
                              <Button onPress={() => directNavigate("/signin")}>Login</Button>
                            </Link>
                          :
                            <Button onPress={login}>Login</Button>
                      }
                      <View style={AppConstants.STYLES.padding2} />
                      {
                        NORMAL_LINKS ?
                            <Link href={isMainLogicalHost ? "/signup" : "/join"} asChild={true} className={"nontext"}>
                              <Button onPress={() => directNavigate(isMainLogicalHost ? "/signup" : "/join")}>{isMainLogicalHost ? "Create account" : "Join"}</Button>
                            </Link>
                          :
                            <Button onPress={join}>{isMainLogicalHost ? "Create account" : "Join"}</Button>
                      }
                    </>
                  )
            }
            <View style={styles.linksWrapper}>
              <CustomDivider fullWidth={true} />
              {
                NORMAL_LINKS ?
                    <>
                      <Link href={AppUtils.getHomeLink(loggedIn, isMainLogicalHost, communityMembership)} asChild={true}>
                        <Pressable onPress={() => directNavigate(AppUtils.getHomeLink(loggedIn, isMainLogicalHost, communityMembership))}>
                          <CustomText type='link'>Home</CustomText>
                        </Pressable>
                      </Link>
                      <Link href="/faqs" asChild={true}>
                        <Pressable onPress={() => directNavigate("/faqs")}>
                          <CustomText type='link'>FAQs</CustomText>
                        </Pressable>
                      </Link>
                      <Link href="/contact" asChild={true}>
                        <Pressable onPress={() => directNavigate("/contact")}>
                          <CustomText type='link'>Contact Us</CustomText>
                        </Pressable>
                      </Link>
                      <Link href="/policies" asChild={true}>
                        <Pressable onPress={() => directNavigate("/policies")}>
                          <CustomText type='link'>Policies</CustomText>
                        </Pressable>
                      </Link>
                      <Link href="/diagnostics" asChild={true}>
                        <Pressable onPress={() => directNavigate("/diagnostics")}>
                          <CustomText type='link'>Diagnostics</CustomText>
                        </Pressable>
                      </Link>
                    </>
                  :
                    <>
                      <Pressable onPress={() => simpleNavigate(AppUtils.getHomeLink(loggedIn, isMainLogicalHost, communityMembership))}><CustomText type='link'>Home</CustomText></Pressable>
                      <Pressable onPress={() => simpleNavigate("/faqs" as RelativePathString)}><CustomText type='link'>FAQs</CustomText></Pressable>
                      <Pressable onPress={() => simpleNavigate("/contact" as RelativePathString)}><CustomText type='link'>Contact Us</CustomText></Pressable>
                      <Pressable onPress={() => simpleNavigate("/policies" as RelativePathString)}><CustomText type='link'>Policies</CustomText></Pressable>
                      <Pressable onPress={() => simpleNavigate("/diagnostics" as RelativePathString)}><CustomText type='link'>Diagnostics</CustomText></Pressable>
                    </>
              }
            </View>
            <View style={AppConstants.STYLES.padding10} />
            <View>
              <CustomDivider fullWidth={true} />
              <View style={AppConstants.STYLES.padding10} />
              <View style={AppConstants.STYLES.alignCenter}><CustomText>Share</CustomText></View>
              {qr}
            </View>
          </BasicScrollView>
        );
      }}
    >
      { children }
    </Drawer>
  );
};
